import type { PhoneKeyTickKind } from '../audio/AudioController'
import type { PhoneTextHistoryGroup, PhoneTextMessage } from '../flow/PhoneConversation'
import type { PhoneReactionCue, PhoneReplyCue } from '../flow/types'

export type DialogueKind = 'dialogue' | 'narration'
export type DialogueSource = 'scene' | 'phone' | 'phone-text' | 'phone-audio'

export interface DialogueLine {
  text: string
  speaker?: string
  kind?: DialogueKind
  source?: DialogueSource
  phoneMessage?: PhoneTextMessage
}

type PhoneStage = 'reading' | 'reaction' | 'typing' | 'sending' | 'error'
type PhoneKeyboardLayout = 'letters' | 'numbers'

export interface DialogueOverlayOptions {
  onPhoneKeyPress?: (key: string, kind: PhoneKeyTickKind) => void
}

const PHONE_KEYBOARD_ROWS = {
  letters: [
    ['Q', 'W', 'E', 'R', 'T', 'Y', 'U', 'I', 'O', 'P'],
    ['A', 'S', 'D', 'F', 'G', 'H', 'J', 'K', 'L'],
    ['Z', 'X', 'C', 'V', 'B', 'N', 'M', '.', '?'],
    ['123', 'space', 'return'],
  ],
  numbers: [
    ['1', '2', '3', '4', '5', '6', '7', '8', '9', '0'],
    ['-', '/', ':', ';', '(', ')', '$', '&', '@', '"'],
    ['#+=', '.', ',', '?', '!', "'"],
    ['ABC', 'space', 'return'],
  ],
} as const satisfies Record<PhoneKeyboardLayout, readonly (readonly string[])[]>

const requiredElement = <T extends HTMLElement>(selector: string): T => {
  const element = document.querySelector<T>(selector)
  if (!element) throw new Error(`Missing required dialogue element: ${selector}`)
  return element
}

export class DialogueOverlay {
  private readonly app = requiredElement<HTMLElement>('#app')
  private readonly overlay = requiredElement<HTMLElement>('#dialogue-overlay')
  private readonly speaker = requiredElement<HTMLElement>('#dialogue-speaker')
  private readonly text = requiredElement<HTMLElement>('#dialogue-text')
  private readonly phoneReader = requiredElement<HTMLElement>('#phone-message-reader')
  private readonly phoneContact = requiredElement<HTMLElement>('#phone-message-contact')
  private readonly phoneAvatar = requiredElement<HTMLElement>('#phone-message-avatar')
  private readonly phoneText = requiredElement<HTMLElement>('#phone-message-text')
  private readonly phoneStatusTime = requiredElement<HTMLTimeElement>('#phone-status-time')
  private readonly phoneTime = requiredElement<HTMLTimeElement>('#phone-message-time')
  private readonly phoneThread = requiredElement<HTMLElement>('#phone-message-thread')
  private readonly phoneHistory = requiredElement<HTMLElement>('#phone-message-history')
  private readonly phoneTodayMarker = requiredElement<HTMLElement>('#phone-message-today')
  private readonly phoneComposer = requiredElement<HTMLElement>('.phone-reader-composer')
  private readonly phoneComposeText = requiredElement<HTMLElement>('#phone-compose-text')
  private readonly phoneKeyboard = requiredElement<HTMLElement>('#phone-keyboard')
  private readonly phoneDeliveryError = requiredElement<HTMLElement>('#phone-delivery-error')
  private phoneClockTimer: number | null = null
  private phoneClockStartedAt = 0
  private phoneClockStartMinute = 85
  private phoneSequenceAbort: AbortController | null = null
  private phoneKeyReleaseTimer: number | null = null
  private phoneKeyboardLayout: PhoneKeyboardLayout = 'letters'
  private phoneKeySoundEnabled = false
  private readonly onPhoneKeyPress?: DialogueOverlayOptions['onPhoneKeyPress']

  public constructor(options: DialogueOverlayOptions = {}) {
    this.onPhoneKeyPress = options.onPhoneKeyPress
  }

  public show(line: DialogueLine): void {
    const cleanText = line.text.trim()
    if (!cleanText) throw new Error('Dialogue text cannot be empty.')

    const delivery: DialogueSource = line.source === 'phone' ? 'phone-text' : line.source ?? 'scene'
    const source = delivery === 'scene' ? 'scene' : 'phone'
    const kind: DialogueKind = line.kind ?? (line.speaker ? 'dialogue' : 'narration')
    const speaker = line.speaker?.trim() || (kind === 'narration' ? 'Narration' : 'Unknown')

    this.overlay.dataset.visible = 'true'
    this.overlay.dataset.source = source
    this.overlay.dataset.delivery = delivery
    this.overlay.dataset.kind = kind
    this.overlay.setAttribute('aria-hidden', 'false')
    this.app.dataset.dialogueVisible = 'true'
    this.speaker.textContent = speaker
    this.text.textContent = cleanText

    if (delivery === 'phone-text') {
      this.phoneContact.textContent = speaker
      this.phoneAvatar.textContent = speaker.slice(0, 1).toUpperCase()
      this.phoneText.textContent = cleanText
      this.phoneTime.textContent = line.phoneMessage?.time?.trim() || '1:25 PM'
      const currentLabel = line.phoneMessage?.currentLabel
      this.phoneTodayMarker.hidden = currentLabel === null
      this.phoneTodayMarker.textContent = currentLabel?.trim() || 'Today'
      this.renderPhoneHistory(line.phoneMessage?.history)
      this.phoneReader.setAttribute('aria-label', `Text message from ${speaker}`)
      this.resetPhoneSequence()
      this.scrollPhoneThreadToOpening(line.phoneMessage?.initialPeekMessageId)
      this.startPhoneStatusClock(line.phoneMessage?.statusTime?.trim() || '1:25')
    } else {
      this.stopPhoneStatusClock()
    }
  }

  public showPhoneReaction(cue: PhoneReactionCue): void {
    if (!this.isPhoneTextVisible) {
      throw new Error(`Phone reaction cue "${cue.id}" needs an open phone-text dialogue.`)
    }

    const cleanText = cue.line.text.trim()
    if (!cleanText) throw new Error(`Phone reaction cue "${cue.id}" cannot be empty.`)

    const speaker = cue.line.speaker?.trim() || 'Unknown'
    this.cancelPhoneSequence()
    this.setPhoneStage('reaction')
    this.overlay.dataset.source = 'scene'
    this.overlay.dataset.kind = cue.line.kind ?? 'dialogue'
    this.speaker.textContent = speaker
    this.text.textContent = cleanText
  }

  public async playPhoneReply(cue: PhoneReplyCue): Promise<boolean> {
    if (!this.isPhoneTextVisible) {
      throw new Error(`Phone reply cue "${cue.id}" needs an open phone-text dialogue.`)
    }

    const message = cue.message.trim()
    const deliveryError = cue.deliveryError.trim()
    if (!message) throw new Error(`Phone reply cue "${cue.id}" message cannot be empty.`)
    if (!deliveryError) throw new Error(`Phone reply cue "${cue.id}" error cannot be empty.`)

    this.cancelPhoneSequence()
    const controller = new AbortController()
    this.phoneSequenceAbort = controller
    const { signal } = controller
    this.phoneKeySoundEnabled = cue.typingSound === 'phone-key-tick'

    this.overlay.dataset.source = 'phone'
    this.phoneDeliveryError.textContent = ''
    this.phoneComposeText.textContent = ''
    this.phoneComposeText.dataset.empty = 'false'
    this.setPhoneStage('typing')
    this.renderPhoneKeyboard('letters')
    this.phoneComposer.scrollTop = 0
    this.phoneThread.scrollTop = this.phoneThread.scrollHeight

    const typingIntervalMs = Math.max(12, cue.typingIntervalMs ?? 48)
    if (
      cue.typingProfile === 'emotional'
      && !(await this.waitForPhoneSequence(520, signal))
    ) return false

    for (const [index, character] of [...message].entries()) {
      if (signal.aborted) return false
      const requiredLayout: PhoneKeyboardLayout = /\d/.test(character) ? 'numbers' : 'letters'
      if (!(await this.switchPhoneKeyboardLayout(requiredLayout, signal))) return false
      this.phoneComposeText.textContent += character
      this.phoneComposer.scrollTop = this.phoneComposer.scrollHeight
      this.pulsePhoneKey(character)
      const delay = cue.typingProfile === 'emotional'
        ? this.getEmotionalTypingDelay(message, index, typingIntervalMs)
        : typingIntervalMs * (/[.!?]/.test(character) ? 2.4 : character === ' ' ? 0.55 : 1)
      if (!(await this.waitForPhoneSequence(delay, signal))) return false
    }

    this.clearActivePhoneKeys()
    this.phoneKeySoundEnabled = false
    this.setPhoneStage('sending')
    if (!(await this.waitForPhoneSequence(Math.max(350, cue.sendingDurationMs ?? 1200), signal))) {
      return false
    }

    this.phoneDeliveryError.textContent = deliveryError
    this.setPhoneStage('error')
    this.phoneDeliveryError.scrollIntoView({ block: 'nearest' })
    return true
  }

  public hide(): void {
    const wasPhoneText = this.overlay.dataset.delivery === 'phone-text'
    this.cancelPhoneSequence()
    this.stopPhoneStatusClock()
    this.overlay.dataset.visible = 'false'
    this.overlay.setAttribute('aria-hidden', 'true')
    this.app.dataset.dialogueVisible = 'false'

    if (wasPhoneText) {
      this.phoneHistory.replaceChildren()
      this.phoneHistory.hidden = true
      this.phoneText.textContent = ''
      this.phoneComposeText.textContent = ''
      this.phoneDeliveryError.textContent = ''
      this.phoneKeyboard.replaceChildren()
      this.phoneKeyboardLayout = 'letters'
      delete this.phoneKeyboard.dataset.layout
      delete this.phoneKeyboard.dataset.switching
    }
  }

  public get isVisible(): boolean {
    return this.overlay.dataset.visible === 'true'
  }

  public get isPhoneTextVisible(): boolean {
    return this.isVisible && this.overlay.dataset.delivery === 'phone-text'
  }

  public dispose(): void {
    this.hide()
  }

  private startPhoneStatusClock(startTime: string): void {
    this.stopPhoneStatusClock()
    this.phoneClockStartMinute = this.parsePhoneStatusTime(startTime)
    this.phoneClockStartedAt = Date.now()
    this.renderPhoneStatusTime(0)
    this.schedulePhoneStatusClockTick()
  }

  private resetPhoneSequence(): void {
    this.cancelPhoneSequence()
    this.setPhoneStage('reading')
    this.phoneComposeText.textContent = 'Message...'
    this.phoneComposeText.dataset.empty = 'true'
    this.phoneDeliveryError.textContent = ''
    this.clearActivePhoneKeys()
    this.renderPhoneKeyboard('letters')
  }

  private cancelPhoneSequence(): void {
    this.phoneSequenceAbort?.abort()
    this.phoneSequenceAbort = null
    this.phoneKeySoundEnabled = false
    this.clearActivePhoneKeys()
  }

  private setPhoneStage(stage: PhoneStage): void {
    this.overlay.dataset.phoneStage = stage
  }

  private renderPhoneKeyboard(layout: PhoneKeyboardLayout): void {
    const fragment = document.createDocumentFragment()

    for (const [rowIndex, keys] of PHONE_KEYBOARD_ROWS[layout].entries()) {
      const row = document.createElement('div')
      row.className = 'phone-keyboard-row'
      row.dataset.row = String(rowIndex + 1)

      for (const keyLabel of keys) {
        const key = document.createElement('span')
        key.className = 'phone-keyboard-key'
        key.dataset.key = keyLabel.toLowerCase()
        key.textContent = keyLabel === 'space' ? '' : keyLabel
        if (keyLabel === 'space') key.setAttribute('aria-label', 'Space')
        row.append(key)
      }

      fragment.append(row)
    }

    this.phoneKeyboard.replaceChildren(fragment)
    this.phoneKeyboardLayout = layout
    this.phoneKeyboard.dataset.layout = layout
  }

  private async switchPhoneKeyboardLayout(
    layout: PhoneKeyboardLayout,
    signal: AbortSignal,
  ): Promise<boolean> {
    if (layout === this.phoneKeyboardLayout) return true

    // Show the same physical interaction a person would make: press 123 before
    // a digit, or ABC before resuming words. The short holds keep both layouts
    // readable instead of flashing past between two typed characters.
    this.pulsePhoneKey(layout === 'numbers' ? '123' : 'abc', 160)
    if (!(await this.waitForPhoneSequence(220, signal))) return false

    this.phoneKeyboard.dataset.switching = 'true'
    this.renderPhoneKeyboard(layout)
    const switched = await this.waitForPhoneSequence(180, signal)
    delete this.phoneKeyboard.dataset.switching
    return switched
  }

  private pulsePhoneKey(character: string, holdMs = 92): void {
    this.clearActivePhoneKeys()
    const keyName = character === ' ' ? 'space' : character.toLowerCase()
    const key = [...this.phoneKeyboard.querySelectorAll<HTMLElement>('[data-key]')]
      .find((candidate) => candidate.dataset.key === keyName)
    if (!key) return

    key.dataset.active = 'true'
    if (this.phoneKeySoundEnabled) {
      const kind: PhoneKeyTickKind = keyName === '123' || keyName === 'abc'
        ? 'layout'
        : keyName === 'space'
          ? 'space'
          : 'character'
      this.onPhoneKeyPress?.(keyName, kind)
    }
    this.phoneKeyReleaseTimer = window.setTimeout(() => {
      this.phoneKeyReleaseTimer = null
      this.clearActivePhoneKeys()
    }, Math.max(60, holdMs))
  }

  private clearActivePhoneKeys(): void {
    if (this.phoneKeyReleaseTimer !== null) {
      window.clearTimeout(this.phoneKeyReleaseTimer)
      this.phoneKeyReleaseTimer = null
    }
    for (const key of this.phoneKeyboard.querySelectorAll<HTMLElement>('[data-active]')) {
      delete key.dataset.active
    }
  }

  private getEmotionalTypingDelay(message: string, index: number, baseMs: number): number {
    const character = message[index]
    const typed = message.slice(0, index + 1)
    const variation = [0.78, 1.08, 0.9, 1.18, 0.84, 1.04, 0.94][index % 7]

    if (character === '.' && message.slice(index, index + 3) === '...') return 190
    if (character === '.' && message[index - 1] === '.' && message[index + 1] === '.') return 250
    if (character === '.' && message.slice(index - 2, index + 1) === '...') return 760
    if (typed.endsWith('Just wow.')) return 880
    if (typed.endsWith('5 years')) return 620
    if (typed.endsWith('how')) return 210
    if (typed.endsWith('decide')) return 340
    if (character === '?') return 720
    if (character === '.' || character === '!') return 520
    if (character === ',') return 330
    if (character === ' ') return baseMs * 0.55

    const emotionalPace = index < 17 ? 1.08 : index < 25 ? 0.98 : index < 52 ? 0.78 : 1.04
    const thinkingBeat = index > 24 && index % 11 === 0 ? 70 : 0
    return baseMs * variation * emotionalPace + thinkingBeat
  }

  private waitForPhoneSequence(durationMs: number, signal: AbortSignal): Promise<boolean> {
    return new Promise((resolve) => {
      if (signal.aborted) {
        resolve(false)
        return
      }

      const timer = window.setTimeout(() => {
        signal.removeEventListener('abort', handleAbort)
        resolve(true)
      }, Math.max(0, durationMs))
      const handleAbort = (): void => {
        window.clearTimeout(timer)
        resolve(false)
      }
      signal.addEventListener('abort', handleAbort, { once: true })
    })
  }

  private stopPhoneStatusClock(): void {
    if (this.phoneClockTimer !== null) {
      window.clearTimeout(this.phoneClockTimer)
      this.phoneClockTimer = null
    }
    this.phoneClockStartedAt = 0
  }

  private schedulePhoneStatusClockTick(): void {
    const elapsedMs = Math.max(0, Date.now() - this.phoneClockStartedAt)
    const delayUntilNextMinute = 60_000 - (elapsedMs % 60_000)

    this.phoneClockTimer = window.setTimeout(() => {
      this.phoneClockTimer = null
      if (!this.isPhoneTextVisible) return

      const elapsedMinutes = Math.floor((Date.now() - this.phoneClockStartedAt) / 60_000)
      this.renderPhoneStatusTime(elapsedMinutes)
      this.schedulePhoneStatusClockTick()
    }, delayUntilNextMinute)
  }

  private parsePhoneStatusTime(value: string): number {
    const match = /^(\d{1,2}):([0-5]\d)$/.exec(value)
    if (!match) return 85

    const hour = Number(match[1])
    const minute = Number(match[2])
    if (hour < 1 || hour > 12) return 85
    return (hour % 12) * 60 + minute
  }

  private renderPhoneStatusTime(elapsedMinutes: number): void {
    const minuteOfHalfDay = (this.phoneClockStartMinute + elapsedMinutes) % (12 * 60)
    const hour = Math.floor(minuteOfHalfDay / 60) || 12
    const minute = minuteOfHalfDay % 60
    this.phoneStatusTime.textContent = `${hour}:${String(minute).padStart(2, '0')}`
  }

  private renderPhoneHistory(groups: readonly PhoneTextHistoryGroup[] | undefined): void {
    this.phoneHistory.replaceChildren()

    for (const group of groups ?? []) {
      const messages = group.messages.filter((message) => message.text.trim())
      if (!messages.length) continue

      const historyGroup = document.createElement('section')
      historyGroup.className = 'phone-reader-history-group'

      const date = document.createElement('span')
      date.className = 'phone-reader-day phone-reader-history-date'
      date.textContent = group.label.trim() || 'Earlier'
      historyGroup.append(date)

      for (const message of messages) {
        const bubble = document.createElement('div')
        bubble.className = 'phone-reader-history-message'
        bubble.dataset.direction = message.direction
        bubble.dataset.messageId = message.id
        const speaker = message.speaker.trim() || (message.direction === 'sent' ? 'You' : 'Unknown')
        const timestamp = message.time?.trim()
        bubble.setAttribute(
          'aria-label',
          `${speaker}${timestamp ? ` at ${timestamp}` : ''}: ${message.text.trim()}`,
        )

        const text = document.createElement('p')
        text.textContent = message.text.trim()
        bubble.append(text)

        if (timestamp) {
          const time = document.createElement('time')
          time.className = 'phone-reader-history-time'
          time.textContent = timestamp
          bubble.append(time)
        }

        historyGroup.append(bubble)
      }

      this.phoneHistory.append(historyGroup)
    }

    this.phoneHistory.hidden = this.phoneHistory.childElementCount === 0
  }

  private scrollPhoneThreadToOpening(initialPeekMessageId?: string): void {
    const historyMessages = this.phoneHistory.querySelectorAll<HTMLElement>('.phone-reader-history-message')
    const requestedPeekMessage = initialPeekMessageId
      ? [...historyMessages].find((message) => message.dataset.messageId === initialPeekMessageId)
      : null
    const peekMessage = requestedPeekMessage ?? historyMessages.item(historyMessages.length - 1)

    if (peekMessage) {
      const visiblePeek = requestedPeekMessage
        ? peekMessage.offsetHeight / 2
        : Math.min(peekMessage.offsetHeight, 56)
      const breathingRoom = requestedPeekMessage ? 0 : 8
      this.phoneThread.scrollTop = Math.max(
        0,
        peekMessage.offsetTop + peekMessage.offsetHeight - visiblePeek - breathingRoom,
      )
      return
    }

    this.phoneThread.scrollTop = Math.max(0, this.phoneTodayMarker.offsetTop - 8)
  }
}
